import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  usePapeleraDeTerreno,
  useRestaurarRegistroDeTerreno,
  useVaciarPapeleraDeTerreno,
} from "@/hooks/usePapeleraDeTerreno";
import { urlDelArchivoDeTerreno, type RegistroEnPapeleraDeTerreno } from "@/lib/terreno";

import { momentoDeSincronizacion, pluralizar } from "./registroDeTerreno";

const TIPOS: Record<string, string> = {
  descripcion: "Texto",
  validacion: "Texto",
  nota_de_voz: "Nota de voz",
  gps: "Ubicación GPS",
  portada: "Portada",
  foto_de_construccion: "Fotografía",
  foto_de_topografia: "Fotografía",
  foto: "Fotografía",
  notas_generales: "Notas generales",
  descripcion_de_construccion: "Descripción de construcción",
  nota_de_dato_de_construccion: "Nota de construcción",
  captura_rapida: "Captura rápida",
  registro: "Registro",
  construccion_terreno: "Construcción creada en terreno",
  agua_terreno: "Agua creada en terreno",
  maquinaria_terreno: "Maquinaria creada en terreno",
  instalacion_terreno: "Instalación creada en terreno",
  plantacion_terreno: "Plantación creada en terreno",
  carpeta_terreno: "Carpeta creada en terreno",
  rol_terreno: "Rol creado en terreno",
  rol_edicion: "Edición de rol",
  maquinaria_ausencia: "Ausencia de maquinaria",
  instalacion_ausencia: "Ausencia de instalación",
};

const etiquetaDelTipo = (tipo: string) => TIPOS[tipo] ?? "Registro de terreno";

function nombreDelBloque(r: RegistroEnPapeleraDeTerreno): string | null {
  const nombre = r.datos.nombre ?? r.datos.especie ?? r.datos.tipo;

  return typeof nombre === "string" && nombre !== "" ? nombre : null;
}

function Contenido({ informeId, r }: { informeId: number; r: RegistroEnPapeleraDeTerreno }) {
  const texto = r.texto ?? r.transcripcion ?? nombreDelBloque(r);

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-ink text-xs font-bold">{etiquetaDelTipo(r.tipo)}</span>
      {r.archivo?.mime.startsWith("image/") && (
        <img
          src={urlDelArchivoDeTerreno(informeId, r.uuid, r.archivo.hash)}
          alt={`Miniatura · ${etiquetaDelTipo(r.tipo)}`}
          className="bg-muted h-16 w-24 rounded-md object-cover"
        />
      )}
      {r.archivo?.mime.startsWith("audio/") && (
        <audio
          controls
          preload="none"
          aria-label="Escuchar nota de voz descartada"
          src={urlDelArchivoDeTerreno(informeId, r.uuid, r.archivo.hash)}
          className="h-8 max-w-full"
        />
      )}
      {texto && <p className="text-ink-body line-clamp-2 text-xs font-medium">{texto}</p>}
      {r.hijos > 0 && (
        <span className="text-ink-muted text-[0.6875rem] font-semibold">
          +{pluralizar(r.hijos, "elemento asociado", "elementos asociados")}
        </span>
      )}
    </div>
  );
}

/**
 * «Papelera (N)»: lo que el ejecutivo descartó del registro de terreno. Se
 * dibuja solo si hay algo (no es una acción bloqueada: es una puerta que
 * todavía no existe), y desde ahí se restaura o se vacía.
 *
 * VACIAR es el único acto irreversible —borra los archivos—, así que es el
 * único botón rojo y pide confirmación.
 */
export function PapeleraDeTerreno({ informeId }: { informeId: number }) {
  const papelera = usePapeleraDeTerreno(informeId);
  const restaurar = useRestaurarRegistroDeTerreno(informeId);
  const vaciar = useVaciarPapeleraDeTerreno(informeId);

  const [abierta, setAbierta] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const total = papelera.data?.total ?? 0;
  // Sin permiso la papelera solo se mira: ni Restaurar ni Vaciar.
  const puedeActuar = papelera.data?.puede_descartar === true;
  const registros = papelera.data?.registros ?? [];

  // Con la papelera vacía el diálogo se cierra: si quedara «abierta», el próximo
  // descarte lo abriría solo.
  if (total === 0 && abierta) setAbierta(false);

  if (total === 0) return null;

  return (
    <>
      {total > 0 && (
        <Button type="button" variant="outline" size="sm" onClick={() => setAbierta(true)}>
          <Trash2 aria-hidden="true" />
          Papelera ({total})
        </Button>
      )}

      <Dialog open={abierta && total > 0} onOpenChange={setAbierta}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Papelera de terreno</DialogTitle>
            <DialogDescription>
              Lo descartado no se usa en el informe ni en «Redactar desde terreno».
              {puedeActuar
                ? " Puedes restaurarlo cuando quieras."
                : " Solo el ejecutivo del informe o un Super Admin pueden restaurarlo o vaciar la papelera."}
            </DialogDescription>
          </DialogHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Elemento</TableHead>
                <TableHead>Capturó</TableHead>
                <TableHead>Descartó</TableHead>
                <TableHead>
                  <span className="sr-only">Acciones</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registros.map((r) => (
                <TableRow key={r.uuid}>
                  <TableCell className="max-w-[18rem] whitespace-normal">
                    <Contenido informeId={informeId} r={r} />
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{r.usuario ?? "—"}</div>
                    <div className="text-ink-hint font-mono">{momentoDeSincronizacion(r.capturado_en)}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{r.descartado_por ?? "—"}</div>
                    <div className="text-ink-hint font-mono">{momentoDeSincronizacion(r.descartado_en)}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    {puedeActuar && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={restaurar.isPending}
                      aria-label={`Restaurar ${etiquetaDelTipo(r.tipo).toLowerCase()}`}
                      onClick={() =>
                        restaurar.mutate(r.uuid, {
                          onError: () => toast.error("No se pudo restaurar. Inténtalo de nuevo."),
                        })
                      }
                    >
                      Restaurar
                    </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {puedeActuar && (
            <DialogFooter>
              <Button type="button" variant="destructive" onClick={() => setConfirmando(true)}>
                Vaciar papelera
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Vaciar la papelera de terreno?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borran definitivamente {pluralizar(total, "elemento descartado", "elementos descartados")}{" "}
              y sus fotos y audios. Esto no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                vaciar.mutate(undefined, {
                  onSuccess: (n) => toast.success(`Papelera vaciada: ${pluralizar(n, "elemento eliminado", "elementos eliminados")}.`),
                  onError: () => toast.error("No se pudo vaciar la papelera. Inténtalo de nuevo."),
                });
                setConfirmando(false);
              }}
            >
              Vaciar papelera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
